/**
 * Profile — view and edit display name, job title, company, avatar, bio,
 * phone (email read-only), plus the theme / language controls the design
 * keeps on this page.
 */

import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BadgeCheck,
  Building2,
  CalendarDays,
  Camera,
  Clock,
  Languages,
  Mail,
  MoonStar,
  Phone,
  Settings as SettingsIcon,
  SunMedium,
  UserRound,
  type LucideIcon,
} from "lucide-react";
import { DARK_MODE_AVAILABLE, useSuiteChromeLayout } from "@weco/suite-chrome";
import { Toast } from "@weco/ui";
import {
  deleteAvatar,
  fetchMyProfile,
  PeopleApiError,
  type ProfilePatchPayload,
  type UserProfile,
  updateMyProfile,
  uploadAvatar,
} from "../api/people.api";
import { useShellStore } from "../store";

const AVATAR_MAX_BYTES = 5 * 1024 * 1024;
const BIO_MAX = 500;
const ACCEPT_IMAGE = "image/jpeg,image/png,image/webp";

/** Center-crop to square and encode as JPEG (backend also resizes). */
export function cropCenterSquareToJpeg(file: File, outputSize = 512, quality = 0.92): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      try {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        const side = Math.min(w, h);
        const sx = (w - side) / 2;
        const sy = (h - side) / 2;
        const canvas = document.createElement("canvas");
        canvas.width = outputSize;
        canvas.height = outputSize;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Canvas not available"));
          return;
        }
        ctx.drawImage(img, sx, sy, side, side, 0, 0, outputSize, outputSize);
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(blob);
            else reject(new Error("Could not encode image"));
          },
          "image/jpeg",
          quality,
        );
      } catch (e) {
        reject(e instanceof Error ? e : new Error(String(e)));
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("Could not load image"));
    };
    img.src = url;
  });
}

function initialsFromName(name: string | null | undefined): string {
  if (!name?.trim()) return "?";
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

/** `/users/me/` also returns the user's organization (UserMeSerializer). */
type ProfileWithOrg = UserProfile & { organization?: { id: string; name: string } | null };

const CARD = "bg-background border border-border rounded-3xl shadow-sm";
const INPUT =
  "w-full h-10 px-3 rounded-xl text-sm border bg-muted/40 outline-none focus:bg-background focus:border-primary transition-colors";

function ProfileField({
  label,
  value,
  icon: Icon,
  ltr,
  wide,
}: {
  label: string;
  value: string;
  icon: LucideIcon;
  ltr?: boolean;
  wide?: boolean;
}) {
  return (
    <div className={`p-4 rounded-2xl bg-muted/30 border border-border min-w-0 text-start ${wide ? "sm:col-span-2" : ""}`}>
      <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
        <Icon className="size-[11px]" aria-hidden />
        {label}
      </p>
      <p className={`text-sm font-semibold ${wide ? "whitespace-pre-line break-words" : "truncate"}`} dir={ltr ? "ltr" : undefined}>
        {value}
      </p>
    </div>
  );
}

/**
 * Profile — design: weco2/profile.jsx (the page the header avatar opens).
 *
 * Identity card, Preferences (appearance + language, the controls the design
 * moved off the header) and Details, all from real data. The design's stats
 * row and "Recent activity" feed are not rendered: no endpoint aggregates
 * them, and invented numbers would be worse than none. Editing (name, job
 * title, company, bio, phone, photo) is the real People API flow, opened from
 * "Edit profile".
 */
export const ProfilePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { effectiveTheme, toggleTheme, locale, toggleLocale } = useSuiteChromeLayout();
  const updateUser = useShellStore((s) => s.updateUser);
  const storeUserId = useShellStore((s) => s.user?.id);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const editRef = useRef<HTMLElement>(null);
  const formId = useId();

  const [profile, setProfile] = useState<ProfileWithOrg | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [company, setCompany] = useState("");
  const [bio, setBio] = useState("");
  const [phone, setPhone] = useState("");

  const [saveLoading, setSaveLoading] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [cropPreviewUrl, setCropPreviewUrl] = useState<string | null>(null);

  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
  } | null>(null);

  const syncForm = (data: UserProfile) => {
    setName(data.name ?? "");
    setJobTitle(data.job_title ?? "");
    setCompany(data.company ?? "");
    setBio(data.bio ?? "");
    setPhone(data.phone ?? "");
  };

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setFormError(null);
    setFieldErrors({});
    try {
      const data = (await fetchMyProfile()) as ProfileWithOrg;
      setProfile(data);
      syncForm(data);
      if (storeUserId && data.id === storeUserId) {
        updateUser({
          name: data.name ?? "",
          avatarUrl: data.avatar_url ?? undefined,
        });
      }
    } catch (err) {
      const msg =
        err instanceof PeopleApiError
          ? err.message
          : t("profile.loadFailed", { defaultValue: "Could not load profile" });
      setFormError(msg);
    } finally {
      setLoading(false);
    }
  }, [storeUserId, updateUser, t]);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    if (!cropFile) {
      setCropPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(cropFile);
    setCropPreviewUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [cropFile]);

  useEffect(() => {
    if (editing) editRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [editing]);

  const handlePickFile = (file: File | undefined) => {
    setAvatarError(null);
    if (!file) return;
    if (file.size > AVATAR_MAX_BYTES) {
      setAvatarError(t("profile.avatarTooLarge", { defaultValue: "Max file size: 5MB" }));
      return;
    }
    setCropFile(file);
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    handlePickFile(e.target.files?.[0]);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handlePickFile(e.dataTransfer.files?.[0]);
  };

  const confirmCrop = async () => {
    if (!cropFile) return;
    setAvatarBusy(true);
    setAvatarError(null);
    try {
      const jpeg = await cropCenterSquareToJpeg(cropFile);
      const { avatar_url } = await uploadAvatar(jpeg, "avatar.jpg");
      setCropFile(null);
      setProfile((p) => (p ? { ...p, avatar_url } : p));
      updateUser({ avatarUrl: avatar_url ?? undefined });
      setToast({ message: t("profile.avatarUpdated", { defaultValue: "Avatar updated" }), type: "success" });
    } catch (err) {
      const msg =
        err instanceof PeopleApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : t("profile.uploadFailed", { defaultValue: "Upload failed" });
      setAvatarError(msg);
    } finally {
      setAvatarBusy(false);
    }
  };

  const cancelCrop = () => {
    setCropFile(null);
  };

  const handleRemoveAvatar = () => {
    if (!window.confirm(t("profile.removeAvatarConfirm", { defaultValue: "Remove your profile photo?" }))) return;
    void (async () => {
      setAvatarBusy(true);
      setAvatarError(null);
      try {
        await deleteAvatar();
        setProfile((p) => (p ? { ...p, avatar_url: null } : p));
        updateUser({ avatarUrl: undefined });
        setToast({ message: t("profile.avatarRemoved", { defaultValue: "Avatar removed" }), type: "success" });
      } catch (err) {
        const msg =
          err instanceof PeopleApiError
            ? err.message
            : t("profile.removeAvatarFailed", { defaultValue: "Could not remove avatar" });
        setAvatarError(msg);
      } finally {
        setAvatarBusy(false);
      }
    })();
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;
    setFormError(null);
    setFieldErrors({});

    const payload: ProfilePatchPayload = {};
    const trimmedName = name.trim();
    if (trimmedName !== (profile.name ?? "").trim()) payload.name = trimmedName;
    if (jobTitle.trim() !== (profile.job_title ?? "")) payload.job_title = jobTitle.trim();
    if (company.trim() !== (profile.company ?? "")) payload.company = company.trim();
    if (bio !== (profile.bio ?? "")) payload.bio = bio;
    if (phone !== (profile.phone ?? "")) payload.phone = phone;

    if (Object.keys(payload).length === 0) {
      setToast({ message: t("profile.noChanges", { defaultValue: "No changes to save" }), type: "info" });
      return;
    }

    setSaveLoading(true);
    try {
      const updated = await updateMyProfile(payload);
      // PATCH answers with the profile serializer too; keep the org we had
      // if a response ever omits it.
      setProfile((prev) => ({ ...(updated as ProfileWithOrg), organization: (updated as ProfileWithOrg).organization ?? prev?.organization }));
      syncForm(updated);
      updateUser({
        name: updated.name ?? "",
        avatarUrl: updated.avatar_url ?? undefined,
      });
      setEditing(false);
      setToast({ message: t("profile.saved", { defaultValue: "Profile saved" }), type: "success" });
    } catch (err) {
      if (err instanceof PeopleApiError) {
        setFormError(err.message);
        const fe: Record<string, string> = {};
        if (err.fieldErrors) {
          for (const [k, msgs] of Object.entries(err.fieldErrors)) {
            fe[k] = msgs.join(" ");
          }
        }
        setFieldErrors(fe);
      } else {
        setFormError(t("profile.saveFailed", { defaultValue: "Could not save profile" }));
      }
    } finally {
      setSaveLoading(false);
    }
  };

  const cancelEdit = () => {
    if (profile) syncForm(profile);
    setFieldErrors({});
    setFormError(null);
    setEditing(false);
  };

  const displayAvatarUrl = profile?.avatar_url ?? undefined;
  const displayName = profile?.name || t("profile.fallbackName", { defaultValue: "User" });
  const orgName = profile?.organization?.name || profile?.company || "";
  const subtitle = [profile?.job_title, orgName].filter(Boolean).join(" · ");
  const isDark = effectiveTheme === "dark";
  const dateLocale = i18n.language?.startsWith("ar") ? "ar" : undefined;
  const joined = profile?.created_at
    ? new Date(profile.created_at).toLocaleDateString(dateLocale, { year: "numeric", month: "long" })
    : null;

  const errorText = (msg: string) => (
    <p role="alert" className="text-sm px-4 py-3 rounded-2xl bg-red-500/10 text-red-600 dark:text-red-400 text-start">
      {msg}
    </p>
  );

  const langPill = (code: "en" | "ar", text: string) => (
    <span
      aria-hidden="true"
      className={`px-2 py-1 rounded-lg text-[11px] font-black tracking-[0.06em] transition-all ${
        locale === code ? "bg-primary text-primary-foreground" : "text-muted-foreground"
      }`}
    >
      {text}
    </span>
  );

  return (
    <div className="flex-1 overflow-y-auto no-scrollbar p-6 max-md:p-4 lg:p-12 bg-muted/10 h-full">
      <div className="max-w-4xl mx-auto w-full mt-4 lg:mt-8 space-y-8 max-md:space-y-5 pb-12">
        {loading && (
          <div className="space-y-8 animate-pulse" aria-busy="true" aria-label={t("profile.loading", { defaultValue: "Loading profile" })}>
            <div className={`${CARD} h-56`} />
            <div className={`${CARD} h-32`} />
            <div className={`${CARD} h-40`} />
          </div>
        )}

        {!loading && profile && (
          <>
            {/* Identity card */}
            <section className={`${CARD} overflow-hidden`}>
              <div className="h-28 bg-primary/10" />
              <div className="px-6 lg:px-8 pb-6 -mt-12 flex flex-col sm:flex-row sm:items-end gap-5">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT_IMAGE}
                  className="sr-only"
                  onChange={handleFileInputChange}
                  aria-label={t("profile.changePhoto", { defaultValue: "Change photo" })}
                />
                <div
                  role="button"
                  tabIndex={0}
                  title={t("profile.changePhotoHint", { defaultValue: "Click or drop an image (JPEG, PNG, WebP, max 5MB)" })}
                  aria-label={t("profile.changePhoto", { defaultValue: "Change photo" })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      fileInputRef.current?.click();
                    }
                  }}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                  onDrop={handleDrop}
                  className="group relative w-24 h-24 shrink-0 rounded-full ring-4 ring-background cursor-pointer overflow-hidden outline-none focus-visible:ring-primary"
                >
                  {displayAvatarUrl ? (
                    <img
                      src={displayAvatarUrl}
                      alt=""
                      className="w-full h-full object-cover border border-border rounded-full"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <span className="w-full h-full rounded-full bg-primary/15 text-primary flex items-center justify-center text-[36px] font-bold">
                      {initialsFromName(name || profile.name)}
                    </span>
                  )}
                  <span className="absolute inset-0 rounded-full bg-black/45 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100 transition-opacity">
                    <Camera className="size-6" aria-hidden />
                  </span>
                </div>
                <div className="flex-1 min-w-0 sm:pb-1 text-start">
                  <h1 className="text-2xl lg:text-3xl font-black tracking-tight truncate">{displayName}</h1>
                  <p className="text-muted-foreground font-medium truncate">
                    {subtitle || <span dir="ltr">{profile.email}</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2 sm:pb-1">
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    aria-expanded={editing}
                    className="px-5 py-3 bg-foreground text-background font-bold rounded-2xl text-sm hover:scale-[1.02] transition-transform"
                  >
                    {t("profile.edit", { defaultValue: "Edit profile" })}
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate("/settings")}
                    title={t("nav.settings", { defaultValue: "Settings" })}
                    aria-label={t("nav.settings", { defaultValue: "Settings" })}
                    className="w-10 h-10 rounded-2xl border border-border flex items-center justify-center hover:bg-muted transition-colors"
                  >
                    <SettingsIcon className="size-[17px]" aria-hidden />
                  </button>
                </div>
              </div>
              {avatarError && <div className="px-6 lg:px-8 pb-6">{errorText(avatarError)}</div>}
            </section>

            {/* Edit form (real PATCH /users/me/) */}
            {editing && (
              <section ref={editRef} className={`${CARD} p-6 max-md:p-4 space-y-4 scroll-mt-6`}>
                <div className="flex items-center justify-between gap-3">
                  <h2 className="text-lg font-bold">{t("profile.edit", { defaultValue: "Edit profile" })}</h2>
                  {displayAvatarUrl && (
                    <button
                      type="button"
                      disabled={avatarBusy}
                      onClick={handleRemoveAvatar}
                      className="px-3 h-9 rounded-xl text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
                    >
                      {t("profile.removeAvatar", { defaultValue: "Remove photo" })}
                    </button>
                  )}
                </div>
                <form id={formId} onSubmit={handleSave} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <FormField
                      id={`${formId}-name`}
                      label={t("profile.displayName", { defaultValue: "Display name" })}
                      error={fieldErrors.name}
                    >
                      <input
                        id={`${formId}-name`}
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                        maxLength={100}
                        autoComplete="name"
                        className={`${INPUT} ${fieldErrors.name ? "border-red-500" : "border-border"}`}
                      />
                    </FormField>
                    <FormField
                      id={`${formId}-job`}
                      label={t("profile.jobTitle", { defaultValue: "Job title" })}
                      error={fieldErrors.job_title}
                    >
                      <input
                        id={`${formId}-job`}
                        type="text"
                        value={jobTitle}
                        onChange={(e) => setJobTitle(e.target.value)}
                        maxLength={255}
                        autoComplete="organization-title"
                        className={`${INPUT} ${fieldErrors.job_title ? "border-red-500" : "border-border"}`}
                      />
                    </FormField>
                    <FormField
                      id={`${formId}-company`}
                      label={t("profile.company", { defaultValue: "Company" })}
                      error={fieldErrors.company}
                    >
                      <input
                        id={`${formId}-company`}
                        type="text"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        maxLength={255}
                        autoComplete="organization"
                        className={`${INPUT} ${fieldErrors.company ? "border-red-500" : "border-border"}`}
                      />
                    </FormField>
                    <FormField
                      id={`${formId}-phone`}
                      label={t("profile.phone", { defaultValue: "Phone" })}
                      error={fieldErrors.phone}
                    >
                      <input
                        id={`${formId}-phone`}
                        type="tel"
                        dir="ltr"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        maxLength={20}
                        autoComplete="tel"
                        className={`${INPUT} ${fieldErrors.phone ? "border-red-500" : "border-border"}`}
                      />
                    </FormField>
                  </div>
                  <FormField id={`${formId}-bio`} label={t("profile.bio", { defaultValue: "Bio" })} error={fieldErrors.bio}>
                    <textarea
                      id={`${formId}-bio`}
                      value={bio}
                      onChange={(e) => setBio(e.target.value.slice(0, BIO_MAX))}
                      rows={4}
                      maxLength={BIO_MAX}
                      className={`${INPUT} h-auto py-2 resize-y min-h-[96px] ${fieldErrors.bio ? "border-red-500" : "border-border"}`}
                    />
                    <p className="text-xs mt-1 text-end text-muted-foreground">
                      {t("profile.bioRemaining", {
                        count: BIO_MAX - bio.length,
                        defaultValue: "{{count}} characters remaining",
                      })}
                    </p>
                  </FormField>
                  <p className="text-xs text-muted-foreground text-start">
                    {t("profile.emailReadOnly", { defaultValue: "Email cannot be changed here." })}
                  </p>
                  {formError && errorText(formError)}
                  <div className="flex flex-wrap items-center gap-2">
                    <button
                      type="submit"
                      disabled={saveLoading}
                      className="px-5 h-11 bg-primary text-primary-foreground font-bold rounded-2xl text-sm hover:bg-primary/90 transition-colors disabled:opacity-60"
                    >
                      {saveLoading
                        ? t("profile.saving", { defaultValue: "Saving…" })
                        : t("profile.save", { defaultValue: "Save changes" })}
                    </button>
                    <button
                      type="button"
                      onClick={cancelEdit}
                      className="px-5 h-11 border border-border font-bold rounded-2xl text-sm hover:bg-muted transition-colors"
                    >
                      {t("profile.cancel", { defaultValue: "Cancel" })}
                    </button>
                  </div>
                </form>
              </section>
            )}

            {/* Preferences: the design's home for theme + language */}
            <section className={`${CARD} p-6 max-md:p-4 space-y-4`}>
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-bold">{t("profile.preferences", { defaultValue: "Preferences" })}</h2>
                <button
                  type="button"
                  onClick={() => navigate("/preferences")}
                  className="text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t("profile.morePreferences", { defaultValue: "More preferences" })}
                </button>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                {/* Dark mode is hidden for now -- see DARK_MODE_AVAILABLE. */}
                {DARK_MODE_AVAILABLE && (
                  <div className="p-4 rounded-2xl bg-muted/30 border border-border flex items-center justify-between gap-3">
                    <div className="min-w-0 text-start">
                      <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
                        {isDark ? <MoonStar className="size-[11px]" aria-hidden /> : <SunMedium className="size-[11px]" aria-hidden />}
                        {t("profile.appearance", { defaultValue: "Appearance" })}
                      </p>
                      <p className="text-sm font-semibold truncate">
                        {isDark
                          ? t("profile.darkMode", { defaultValue: "Dark mode" })
                          : t("profile.lightMode", { defaultValue: "Light mode" })}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={toggleTheme}
                      title={
                        isDark
                          ? t("profile.toLight", { defaultValue: "Switch to light mode" })
                          : t("profile.toDark", { defaultValue: "Switch to dark mode" })
                      }
                      aria-label={
                        isDark
                          ? t("profile.toLight", { defaultValue: "Switch to light mode" })
                          : t("profile.toDark", { defaultValue: "Switch to dark mode" })
                      }
                      className="w-10 h-10 rounded-2xl border border-border flex items-center justify-center hover:bg-muted transition-colors shrink-0"
                    >
                      {isDark ? <SunMedium className="size-[17px]" aria-hidden /> : <MoonStar className="size-[17px]" aria-hidden />}
                    </button>
                  </div>
                )}
                <div className="p-4 rounded-2xl bg-muted/30 border border-border flex items-center justify-between gap-3">
                  <div className="min-w-0 text-start">
                    <p className="text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
                      <Languages className="size-[11px]" aria-hidden />
                      {t("profile.language", { defaultValue: "Language" })}
                    </p>
                    <p className="text-sm font-semibold truncate">{locale === "ar" ? "العربية" : "English"}</p>
                  </div>
                  <button
                    type="button"
                    onClick={toggleLocale}
                    title={locale === "en" ? t("locale.toArabic") : t("locale.toEnglish")}
                    aria-label={locale === "en" ? t("locale.toArabic") : t("locale.toEnglish")}
                    className="h-10 px-1 rounded-2xl border border-border flex items-center gap-0.5 hover:bg-muted transition-colors shrink-0"
                  >
                    {langPill("en", "EN")}
                    {langPill("ar", "ع")}
                  </button>
                </div>
              </div>
            </section>

            {/* Details */}
            <section className={`${CARD} p-6 max-md:p-4 space-y-4`}>
              <h2 className="text-lg font-bold">{t("profile.details", { defaultValue: "Details" })}</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <ProfileField label={t("profile.email", { defaultValue: "Email" })} value={profile.email ?? "-"} icon={Mail} ltr />
                <ProfileField label={t("profile.organization", { defaultValue: "Organization" })} value={orgName || "-"} icon={Building2} />
                <ProfileField label={t("profile.role", { defaultValue: "Role" })} value={profile.job_title || "-"} icon={BadgeCheck} />
                <ProfileField label={t("profile.timezone", { defaultValue: "Time zone" })} value={profile.timezone || "-"} icon={Clock} ltr />
                {profile.phone && (
                  <ProfileField label={t("profile.phone", { defaultValue: "Phone" })} value={profile.phone} icon={Phone} ltr />
                )}
                {joined && (
                  <ProfileField label={t("profile.memberSince", { defaultValue: "Member since" })} value={joined} icon={CalendarDays} />
                )}
                {profile.bio && (
                  <ProfileField label={t("profile.bio", { defaultValue: "Bio" })} value={profile.bio} icon={UserRound} wide />
                )}
              </div>
            </section>
          </>
        )}

        {!loading && !profile && formError && errorText(formError)}
      </div>

      {cropFile && cropPreviewUrl && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/55"
          role="dialog"
          aria-modal="true"
          aria-labelledby="crop-dialog-title"
          onClick={cancelCrop}
        >
          <div
            className="w-full max-w-md rounded-3xl border border-border bg-background p-6 shadow-xl text-start"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="crop-dialog-title" className="text-lg font-bold mb-2">
              {t("profile.adjustAvatar", { defaultValue: "Adjust avatar" })}
            </h3>
            <p className="text-sm text-muted-foreground mb-5">
              {t("profile.adjustAvatarHint", {
                defaultValue: "A square crop from the center will be used. You can confirm or choose another file.",
              })}
            </p>
            <div className="flex justify-center mb-6">
              <div className="w-48 h-48 rounded-full overflow-hidden border-4 border-border bg-muted shrink-0">
                <img src={cropPreviewUrl} alt="" className="w-full h-full object-cover object-center" />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                onClick={cancelCrop}
                disabled={avatarBusy}
                className="px-5 h-10 border border-border font-bold rounded-2xl text-sm hover:bg-muted transition-colors disabled:opacity-50"
              >
                {t("profile.cancel", { defaultValue: "Cancel" })}
              </button>
              <button
                type="button"
                onClick={() => void confirmCrop()}
                disabled={avatarBusy}
                className="px-5 h-10 bg-primary text-primary-foreground font-bold rounded-2xl text-sm hover:bg-primary/90 transition-colors disabled:opacity-60"
              >
                {avatarBusy
                  ? t("profile.uploading", { defaultValue: "Uploading…" })
                  : t("profile.confirm", { defaultValue: "Confirm" })}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <Toast message={toast.message} type={toast.type} duration={4000} onClose={() => setToast(null)} />
      )}
    </div>
  );
};

function FormField({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="text-start">
      <label
        htmlFor={id}
        className="block text-[10px] font-black uppercase tracking-widest text-muted-foreground mb-2"
      >
        {label}
      </label>
      {children}
      {error && <p className="text-xs mt-1 text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
}

export default ProfilePage;
